import { useState, useEffect, useRef, useCallback } from 'react';

const WHISPER_WS_URL = 'ws://luna.mesh:5003';

/**
 * useCallTranscription — captures audio from active RC call
 * and streams to Whisper on Luna for live transcription.
 */
export function useCallTranscription({ callId, sessionId, enabled, onTranscript }) {
  const [isConnected, setIsConnected] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  const [transcript, setTranscript] = useState([]);
  const [error, setError] = useState(null);

  const wsRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioContextRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const enabledRef = useRef(enabled);
  const onTranscriptRef = useRef(onTranscript);

  // Keep refs current
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);
  useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);

  const cleanup = useCallback(() => {
    // Clear poll
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }

    // Clear reconnect timer
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    // Stop MediaRecorder
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (e) { /* ignore */ }
    }
    mediaRecorderRef.current = null;

    // Close AudioContext (but don't nullify sourceNode — can't recreate per element)
    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      try { audioContextRef.current.close(); } catch (e) { /* ignore */ }
    }
    audioContextRef.current = null;
    sourceNodeRef.current = null;

    // Send end and close WebSocket
    if (wsRef.current) {
      try {
        if (wsRef.current.readyState === WebSocket.OPEN) {
          wsRef.current.send(JSON.stringify({ type: 'end', session_id: sessionId }));
        }
        wsRef.current.close();
      } catch (e) { /* ignore */ }
      wsRef.current = null;
    }

    setIsCapturing(false);
    setIsConnected(false);
  }, [sessionId]);

  useEffect(() => {
    if (!enabled || !callId || !sessionId) {
      cleanup();
      setTranscript([]);
      setError(null);
      return;
    }

    let destroyed = false;

    // ── WebSocket connection ──
    function connectWebSocket() {
      if (destroyed) return;

      const ws = new WebSocket(WHISPER_WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        if (destroyed) { ws.close(); return; }
        ws.send(JSON.stringify({
          type: 'start',
          session_id: sessionId,
          call_id: callId,
        }));
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'ready') {
            setIsConnected(true);
            startCapture();
          } else if (data.type === 'transcript' && data.text) {
            const segment = {
              text: data.text,
              speaker: data.speaker || 'remote',
              timestamp: new Date().toISOString(),
            };
            setTranscript(prev => [...prev, segment]);
            onTranscriptRef.current?.(segment);
          } else if (data.type === 'error') {
            setError(data.message);
          }
        } catch (e) {
          console.warn('useCallTranscription: bad message', e);
        }
      };

      ws.onclose = () => {
        setIsConnected(false);
        if (!destroyed && enabledRef.current) {
          reconnectTimerRef.current = setTimeout(connectWebSocket, 3000);
        }
      };

      ws.onerror = (e) => {
        console.warn('useCallTranscription: WebSocket error', e);
      };
    }

    // ── Audio capture from #rc-remote-audio ──
    function startCapture() {
      if (destroyed || isCapturing) return;

      let attempts = 0;
      const maxAttempts = 100; // 100 * 100ms = 10s

      pollIntervalRef.current = setInterval(() => {
        attempts++;
        const audioEl = document.getElementById('rc-remote-audio');

        if (audioEl) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          attachToAudioElement(audioEl);
        } else if (attempts >= maxAttempts) {
          clearInterval(pollIntervalRef.current);
          pollIntervalRef.current = null;
          setError('Audio element not found after 10s');
        }
      }, 100);
    }

    function attachToAudioElement(audioEl) {
      if (destroyed) return;

      try {
        // Check MediaRecorder support
        if (!window.MediaRecorder) {
          setError('MediaRecorder not supported');
          return;
        }

        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        audioContextRef.current = ctx;

        // Resume if suspended (autoplay policy)
        if (ctx.state === 'suspended') {
          ctx.resume();
        }

        // createMediaElementSource can only be called once per element
        let source;
        if (audioEl._whisperSourceNode) {
          // Already attached — can't create again, need fresh context approach
          // Use captureStream() instead
          const stream = audioEl.captureStream ? audioEl.captureStream() : audioEl.mozCaptureStream();
          source = ctx.createMediaStreamSource(stream);
        } else {
          source = ctx.createMediaElementSource(audioEl);
          audioEl._whisperSourceNode = true;
          // Must reconnect to destination to preserve playback
          source.connect(ctx.destination);
        }

        const destination = ctx.createMediaStreamDestination();
        source.connect(destination);
        sourceNodeRef.current = source;

        // Set up MediaRecorder
        const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
          ? 'audio/webm;codecs=opus'
          : 'audio/webm';

        const recorder = new MediaRecorder(destination.stream, { mimeType });
        mediaRecorderRef.current = recorder;

        recorder.ondataavailable = (event) => {
          if (event.data.size > 0 && wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(event.data);
          }
        };

        recorder.onerror = (e) => {
          console.warn('useCallTranscription: MediaRecorder error', e);
        };

        recorder.start(3000); // 3-second chunks
        setIsCapturing(true);
        setError(null);

      } catch (e) {
        console.error('useCallTranscription: capture setup failed', e);
        setError(`Capture failed: ${e.message}`);
      }
    }

    connectWebSocket();

    return () => {
      destroyed = true;
      cleanup();
    };
  }, [enabled, callId, sessionId, cleanup]);

  return { isConnected, isCapturing, transcript, error };
}
